import { useState } from 'react';
import { SegmentedControl } from '@/components/molecules/SegmentedControl';
import { ChartCard, ChartValueTable } from '@/components/molecules/org-admin/charts/ChartCard';
import { TrendAreaChart } from '@/components/molecules/org-admin/charts/TrendAreaChart';
import { CumulativeSpendChart } from '@/components/molecules/org-admin/charts/OverviewCharts';
import { Section } from '@/components/molecules/org-admin/OrgAdminSection';
import type {
  CumulativeSpendPoint,
  TrendMeasure,
  TrendPoint,
} from '@/services/org-admin/analysis';
import { formatCurrencyUsd, formatTokenCount } from '../usageFormat';

/**
 * "Is it growing?" — the daily series, and the cumulative view with its month-end projection.
 *
 * Both charts moved here. The daily trend came from Analysis; the cumulative chart came from
 * Overview, where it was titled "Total spend" — a third thing on the panel carrying that phrase,
 * alongside Overview's own reporting-range tile and Analysis's budget-cycle hero. They belong
 * together because they answer one question at two resolutions: what happened each day, and where
 * the cycle is heading. Keeping them on separate tabs meant an admin comparing them had to remember
 * one while looking at the other.
 */

const TREND_MEASURES: Array<{ value: TrendMeasure; label: string }> = [
  { value: 'spendUsd', label: 'Spend' },
  { value: 'tokens', label: 'Tokens' },
  { value: 'runs', label: 'Runs' },
];

/** Trend table dates — matches the chart's tooltip rather than showing a raw ISO day. */
const TABLE_DATE = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});

function formatCount(value: number): string {
  return value.toLocaleString();
}

/**
 * Axis ticks convey the level, not the cent — but they have to convey SOMETHING.
 *
 * `Math.round` alone rendered every tick as "$0" whenever the whole axis sat under a dollar, which is
 * the normal case for per-feature cost on a small org: a live chart showed four ticks reading
 * "$0 $0 $0 $0" with no readable scale at all. Sub-dollar values therefore keep enough precision to
 * be distinguishable.
 */
export function formatCurrencyAxis(value: number): string {
  const magnitude = Math.abs(value);
  if (magnitude >= 1000) return `$${Math.round(value / 100) / 10}k`;
  if (magnitude >= 1) return `$${Math.round(value)}`;
  if (value === 0) return '$0';
  return magnitude >= 0.01 ? `$${value.toFixed(2)}` : `$${value.toFixed(3)}`;
}

function formatCountAxis(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${Math.round(value / 100_000) / 10}M`;
  if (Math.abs(value) >= 1000) return `${Math.round(value / 100) / 10}k`;
  return String(Math.round(value));
}

const TREND_MEASURE_CONFIG: Record<
  TrendMeasure,
  { label: string; format: (value: number) => string; formatAxis: (value: number) => string }
> = {
  spendUsd: { label: 'Spend', format: formatCurrencyUsd, formatAxis: formatCurrencyAxis },
  tokens: { label: 'Tokens', format: formatTokenCount, formatAxis: formatTokenCount },
  runs: { label: 'Runs', format: formatCount, formatAxis: formatCountAxis },
};

export interface TrendSectionProps {
  trend: TrendPoint[] | null;
  cumulative: CumulativeSpendPoint[] | null;
  /** Sum of every seat's budget, for the limit rule on the cumulative chart. 0 when none is set. */
  budgetUsd: number;
  rangeLabel: string;
  width: number | null;
  isLoading: boolean;
  isFetching: boolean;
}

export function TrendSection({
  trend,
  cumulative,
  budgetUsd,
  rangeLabel,
  width,
  isLoading,
  isFetching,
}: TrendSectionProps) {
  const [measure, setMeasure] = useState<TrendMeasure>('spendUsd');
  const hasTrend = !!trend?.length;

  return (
    <Section title="Is it growing?">
      <ChartCard
        testId="trend"
        title="Day by day"
        description={
          hasTrend
            ? `Daily ${TREND_MEASURE_CONFIG[measure].label.toLowerCase()} ${rangeLabel}.`
            : `Daily spend ${rangeLabel}.`
        }
        controls={
          /*
           * No measure toggle when there is no series to measure — otherwise the reader can switch
           * between three equally empty charts.
           */
          hasTrend ? (
            <SegmentedControl
              ariaLabel="Trend measure"
              value={measure}
              options={TREND_MEASURES}
              onChange={setMeasure}
              size="sm"
            />
          ) : undefined
        }
        isLoading={isLoading}
        isFetching={isFetching}
        isEmpty={!isLoading && !hasTrend}
        emptyTitle="Daily breakdown isn’t available yet"
        emptyDescription="The usage API reports totals per period rather than per day. A daily trend will appear here once that endpoint is available."
        table={
          trend?.length ? (
            <ChartValueTable
              caption={`Daily ${TREND_MEASURE_CONFIG[measure].label} over the ${rangeLabel}`}
              columns={[
                { label: 'Date' },
                { label: TREND_MEASURE_CONFIG[measure].label, align: 'right' },
              ]}
              rows={trend.map((point) => ({
                key: point.date,
                cells: [
                  TABLE_DATE.format(new Date(`${point.date}T00:00:00`)),
                  TREND_MEASURE_CONFIG[measure].format(point[measure]),
                ],
              }))}
            />
          ) : undefined
        }
      >
        {trend?.length ? (
          <TrendAreaChart
            data={trend}
            measure={measure}
            measureLabel={TREND_MEASURE_CONFIG[measure].label}
            formatValue={TREND_MEASURE_CONFIG[measure].format}
            formatAxis={TREND_MEASURE_CONFIG[measure].formatAxis}
            containerWidth={width}
          />
        ) : null}
      </ChartCard>

      <div className="mt-3">
        <ChartCard
          testId="cumulative"
          /* "Cumulative spend", not "Total spend" — see this file's header for why that phrase is
             no longer allowed to name a figure on its own. */
          title="Cumulative spend"
          /*
           * The projection clause is CONDITIONAL, from `dev`'s closed-month fix.
           *
           * A closed month is pure actuals — there is nothing left to project into — so the adapter
           * emits no projected points for it (`buildCumulative`'s `isOpenRange`). Promising a
           * projection in the description while the chart shows none is the description lying about
           * the chart, and it is the kind of claim §10 exists to prevent.
           */
          description={
            cumulative?.some((point) => point.projected)
              ? `Running total ${rangeLabel}, projected to period end at the last 7 days’ run rate.`
              : `Running total ${rangeLabel}. Actuals only — the period is complete.`
          }
          isLoading={isLoading}
          isFetching={isFetching}
          isEmpty={!isLoading && !cumulative?.length}
          emptyTitle="Daily spend isn’t available"
          emptyDescription="This chart needs a per-day series. Every other figure on this screen comes from range totals, which the usage API does return."
          table={
            <ChartValueTable
              caption="Cumulative spend by day"
              columns={[
                { label: 'Date' },
                { label: 'Cumulative', align: 'right' },
                { label: 'Source', align: 'right' },
              ]}
              rows={(cumulative ?? []).map((point) => ({
                key: point.date,
                cells: [
                  point.date,
                  formatCurrencyUsd(point.cumulativeUsd),
                  point.projected ? 'Projected' : 'Actual',
                ],
              }))}
            />
          }
        >
          <CumulativeSpendChart
            points={cumulative ?? []}
            budgetUsd={budgetUsd}
            formatValue={formatCurrencyUsd}
            formatAxis={formatCurrencyAxis}
            containerWidth={width}
          />
        </ChartCard>
      </div>
    </Section>
  );
}
